<template>
  <!-- Switch between Loog and Text -->
  <div id="add-decorations">
    <div id="add-decorations-switch">
      <transition name="fade">
        <label
          for="add-decoration-logo"
          :class="{
            selected: addDecorationSwitch === 'logo',
            disabled: activeDecoration && !activeDecoration.isNewDecoration,
          }"
          v-if="layoutAllowsLogo"
        >
          <img
            src="@/assets/icons/ui/decoration-icon-uploads.png"
            alt="image icon"
          />
          <span>Graphic</span>
          <input
            type="radio"
            name="add-decoration-type"
            id="add-decoration-logo"
            v-model="addDecorationSwitch"
            :disabled="activeDecoration && !activeDecoration.isNewDecoration"
            value="logo"
          />
        </label>
      </transition>
      <transition name="fade">
        <label
          for="add-decoration-text"
          :class="{
            selected: addDecorationSwitch === 'text',
            disabled: activeDecoration && !activeDecoration.isNewDecoration,
          }"
          v-if="layoutAllowsText"
        >
          <img
            src="@/assets/icons/ui/decoration-icon-text.png"
            alt="text icon"
          />
          <span>Text</span>
          <input
            type="radio"
            name="add-decoration-type"
            id="add-decoration-text"
            v-model="addDecorationSwitch"
            :disabled="activeDecoration && !activeDecoration.isNewDecoration"
            value="text"
          />
        </label>
      </transition>
    </div>

    <!-- Add Logo -->
    <template v-if="addDecorationSwitch === 'logo'">
      <!-- Customize Logo - Threekit properties  -->
      <template v-if="showCustomizeLogo">
        <!-- Graphic Preview -->
        <div class="add-decoration--logo-filename">
          <p>{{ graphicFileName }}</p>
        </div>
        <div id="add-decoration-logo-preview">
          <div id="logo-preview-container">
            <button class="swap-graphic btn" @click="showCustomizeLogo = false">
              <img src="~@/assets/edit.svg" alt="Customize icon" />
              <span>Swap Graphic</span>
            </button>
            <img
              id="logo-preview-image"
              :src="graphicPreview"
              alt="graphic preview"
            />
          </div>
          <!-- Logo tools (Edit, Mirror, Rotate) -->
          <div id="logo-tools">
            <button
              class="tool-button"
              title="edit graphic"
              @click="handleOpenEditLogoModal()"
            >
              <img
                src="@/assets/icons/ui/color-wand-outline.png"
                alt="edit graphic"
              />
            </button>

            <button
              class="tool-button flip-y"
              @click="handleFlip('Y')"
              title="mirror Y graphic"
            >
              <img
                src="@/assets/icons/ui/icon_mirror.png"
                alt="mirror Y graphic"
              />
            </button>
            <button
              class="tool-button flip-x"
              @click="handleFlip('X')"
              title="mirror X graphic"
            >
              <img
                src="@/assets/icons/ui/icon_mirror.png"
                alt="mirror X graphic"
              />
            </button>

            <button
              class="tool-button"
              @click="handleChangeRotation()"
              title="rotate graphic"
            >
              <img
                src="@/assets/icons/ui/refresh-outline.png"
                alt="rotate graphic"
              />
            </button>
          </div>
        </div>
        <!-- Graphic Colors -->
        <ColorPicker
          :colorPickerMode="'multiple'"
          :colorsToChange="activeGraphicFromLibraryColors"
          @selectColor="(color) => handleChangeGraphicColor(color)"
        />
        <div class="inputs-row-container">
          <!-- Location Dropdwon -->
          <label
            class="input-label"
            id="graphic-location-dropdown"
            v-if="availableLocations && availableLocations.length"
          >
            <span>Location</span>
            <v-select
              :value="activeDecoration && activeDecoration.locationName"
              @input="handleChangeLocation"
              label="name"
              :options="availableLocations"
              :clearable="false"
              :searchable="false"
              :disabled="availableLocations && availableLocations.length < 2"
              class="box-shadow"
            ></v-select>
          </label>

          <!-- Sizes Dropdown -->
          <label
            class="input-label"
            v-if="showSizesDropdown"
            id="graphic-sizes-dropdown"
          >
            <span>Height</span>
            <v-select
              :value="sizeValue"
              @input="handleChangeSize"
              :options="sizeOptions"
              :clearable="false"
              :searchable="false"
              class="box-shadow"
              :disabled="sizeOptions && sizeOptions.length < 2"
            ></v-select>
          </label>

          <!-- Material Dropdown -->
          <label
            class="input-label"
            v-if="showMaterialsDropdown && decorationIsLogo"
            id="graphic-material-dropdown"
          >
            <span>Type</span>
            <v-select
              :value="materialsConfiguration && materialsConfiguration.value"
              @input="handleChangeMaterial"
              :options="materialsOptions"
              :clearable="false"
              :searchable="false"
              class="box-shadow"
              :disabled="materialsOptions && materialsOptions.length < 2"
            >
              <!-- Show this only if the dropdown is disabled -->
              <template
                #selected-option
                v-if="materialsOptions && materialsOptions.length < 2"
              >
                <div class="disabled-display-container">
                  <img
                    src="@/assets/icons/ui/lock-closed-outline.svg"
                    alt="lock"
                  />
                  <span class="text-placeholder">{{
                    materialsConfiguration && materialsConfiguration.value
                  }}</span>
                </div>
              </template>
            </v-select>
          </label>
        </div>
        <div class="inputs-row-container">
          <!-- Material Dropdown -->
          <label
            class="input-label"
            v-if="showMaterialsDropdown && !decorationIsLogo"
            id="graphic-material-dropdown"
          >
            <span>Type</span>
            <v-select
              :value="materialsConfiguration && materialsConfiguration.value"
              @input="handleChangeMaterial"
              :options="materialsOptions"
              :clearable="false"
              :searchable="false"
              class="box-shadow"
              :disabled="materialsOptions && materialsOptions.length < 2"
            >
              <!-- Show this only if the dropdown is disabled -->
              <template
                #selected-option
                v-if="materialsOptions && materialsOptions.length < 2"
              >
                <div class="disabled-display-container">
                  <img
                    src="@/assets/icons/ui/lock-closed-outline.svg"
                    alt="lock"
                  />
                  <span class="text-placeholder">{{
                    materialsConfiguration && materialsConfiguration.value
                  }}</span>
                </div>
              </template>
            </v-select>
          </label>
        </div>
      </template>

      <!-- Upload New Logo -->
      <template v-else>
        <UploadLogoButton
          :applyMode="'activeProduct'"
          :refName="'upload-new-logo'"
          :graphicButtonHint="'Upload New Graphic'"
          :type="'upload'"
        />
        <transition name="fade">
          <UploadLogosModal @saveLogo="handleSaveLogo"
        /></transition>
        <div class="info-text mb-3">
          <p class="py-2">
            <span class="slate-bold"
              >Vector graphics (such as AI or SVG) are preferred</span
            >
            since they are of higher quality and ready for production.
          </p>
          <p class="py-2">
            If you do not have a vector, our graphic artists will vectorize it
            for you, but this could add extra time for production and might
            require final Art Approval before we can begin.
          </p>
        </div>

        <!-- Logos list -->
        <p class="header-label" v-if="projectLogos.length">
          Recently Uploaded Graphics:
        </p>
        <div class="logos-container mb-3">
          <SidebarLogoPreview
            :logo="logo"
            v-for="(logo, index) in projectLogos"
            :key="index + '-logo-selection'"
            @selectLogo="handleChangeLogo({ logo })"
          />
          <!-- :overrideBackground="overrideLogoPreviewsBackground" -->
        </div>

        <!-- Graphics Library -->
        <article id="graphics-library-info">
          <p class="slate-bold">Don’t have a Graphic? We can help.</p>

          <p>
            Browse our extensive library of mascots and logos to save you time
            and hassle. These graphics have been pre-approved for production by
            our graphic artists and can be customized in your colors
          </p>
          <button class="btn btn-black" @click="handleOpenGraphicsLibrary()">
            Browse Production Ready Graphics
          </button>
        </article>
      </template>

      <!-- Edit Logo -->
      <template v-if="showEditLogo">
        <UploadLogosModal @saveLogo="handleSaveLogo" />
      </template>
    </template>

    <!-- Add Text -->
    <template v-if="addDecorationSwitch === 'text'">
      <!-- Select type of text -->

      <div class="text-type-buttons-container">
        <div
          class="select-text-type flex flex-column flex-lg-row justify-content-between"
        >
          <button
            v-for="(textType, index) in textTypes"
            :key="index + '-textype'"
            class="btn btn-black my-2 text-uppercase slate-bold"
            :class="{
              unselected: currentContentType !== textType,
            }"
            :disabled="
              !availableLocationsByContentType ||
              !getAvailableLocationsByContentType(textType).length
            "
            @click="handleSelectTypeOfTextDecoration(textType)"
          >
            <img
              v-if="textType && decorationLabel(textType)"
              :src="
                require(`@/assets/icons/ui/${decorationLabel(textType)}.svg`)
              "
              alt="decoration label"
            />
            <span>{{ textType }}</span>
          </button>
        </div>
      </div>

      <AddTextStamp
        v-if="currentContentType"
        :typeOfProduct="'activeProduct'"
        :activeDecorationConfigurator="activeDecorationConfigurator"
        :activeDecorationAttributes="activeDecorationAttributes"
        :currentLocation="currentLocation"
        :availableLocationsByContentType="availableLocationsByContentType"
        :decoration="activeDecoration"
        :locationOptions="availableLocations"
        :materialsConfiguration="materialsConfiguration"
        :materialsOptions="materialsOptions"
        :sizeConfiguration="sizeConfiguration"
        :sizeOptions="sizeOptions"
        :sizeValue="sizeValue"
        :showLocationDropdown="true"
        :showMaterialsDropdown="true"
        :showSizesDropdown="true"
        :textValue="textValue"
        @changeLocation="handleChangeLocation"
        @changeMaterial="handleChangeMaterial"
        @changeSize="handleChangeSize"
      />
      <!-- @changeTextStampSelected="(value) => (textStampSelectedOption = value)" -->
      <!-- :product="product"
        :layout="layout" -->
      <!-- :uniqueOptionToShow="textStampSelectedOptionLabel" -->
      <!-- :showOnlyOnceOption="showOnlyOnceTextStampOption" -->
      <!-- :textStampSelectedOption="textStampSelectedOption" -->
    </template>

    <!-- Graphics Library -->
    <transition name="fade">
      <GraphicsLibraryModal
        v-if="showGraphicsLibraryModal"
        @closeModal="showGraphicsLibraryModal = false"
        @setGraphic="handleSelectGraphic"
      />
    </transition>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import AddTextStamp from "@/components/Projects/Project/ActiveProduct/Decorations/AddTextStamp.vue";
import ColorPicker from "@/components/Projects/Project/Colors/ColorPicker.vue";
import GraphicsLibraryModal from "@/components/Projects/Project/ActiveProduct/Decorations/GraphicsLibraryModal.vue";
import SidebarLogoPreview from "@/components/Projects/Project/Sidebar/SidebarLogoPreview.vue";
import UploadLogoButton from "@/components/Utils/Logos/UploadLogoButton.vue";
import UploadLogosModal from "@/components/Utils/Logos/UploadLogosModal";

export default {
  name: "AddDecoration",

  components: {
    AddTextStamp,
    ColorPicker,
    GraphicsLibraryModal,
    SidebarLogoPreview,
    UploadLogoButton,
    UploadLogosModal,
  },

  props: {
    activeDecorationIsEmpty: {
      type: Boolean,
      default: true,
    },
    layoutAllowsLogo: {
      type: Boolean,
      default: true,
    },
    layoutAllowsText: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      addDecorationSwitch: "logo",
      showCustomizeLogo: false,
      showEditLogo: false,
      showMaterialsDropdown: true,
      showSizesDropdown: true,
      showGraphicsLibraryModal: false,
      textTypes: ["Custom Text", "Player Number", "Player Name"],
    };
  },

  computed: {
    ...mapState("project", ["projectLogos"]),
    ...mapState("threekit", ["activeDecoration", "activeDecorationAttributes"]),
    ...mapGetters("threekit", [
      "activeDecorationAvailableLocations",
      "decorations3DListByReversibleView",
      "findThreekitColorByAssetId",
      "getNestedConfigurator",
      "getThreekitColorAssetIdByGameplanId",
    ]),
    ...mapState("uploadLogos", ["logoModalAction"]),

    activeDecorationConfigurator() {
      if (!this.activeDecoration) return null;
      const locationName = this.activeDecoration?.locationName;

      const configurator = this.getNestedConfigurator({
        parentConfigurator: this.$store.state.threekit.decorationsConfigurator,
        attributeName: locationName,
      });

      return configurator;
    },

    activeGraphicFromLibraryConfigurator() {
      if (
        this.currentContentType === "Uploads" ||
        this.currentContentType === "Graphics"
      ) {
        const graphicConfigurator = this.getNestedConfigurator({
          parentConfigurator: this.activeDecorationConfigurator,
          attributeName: "Logo",
        });
        return graphicConfigurator;
      } else {
        return null;
      }
    },

    activeGraphicFromLibraryLayers() {
      if (!this.activeGraphicFromLibraryConfigurator) return null;
      return this.activeGraphicFromLibraryConfigurator
        ?.getDisplayAttributes()
        ?.filter((layer) => layer?.name?.includes("Layer"));
    },

    activeGraphicFromLibraryColors() {
      if (!this.activeGraphicFromLibraryConfigurator) return null;

      const layersConfigurators = this.activeGraphicFromLibraryLayers?.map(
        (layerAttr) => {
          return this.activeGraphicFromLibraryConfigurator?.getNestedConfigurator(
            layerAttr
          );
        }
      );

      return layersConfigurators?.map((layer, i) => {
        const assetId = layer
          ?.getDisplayAttributes()
          ?.find((attr) => attr?.name === "Fill")?.value?.assetId;

        return {
          color: { ...this.findThreekitColorByAssetId(assetId) },
          key: `${i}`,
        };
      });
    },

    decorationsConfiguratorAttributes() {
      if (!this.activeDecoration) return null;
      return this.$store.state.threekit.decorationsConfigurator?.getDisplayAttributes();
    },

    availableLocationsByContentType() {
      if (!this.activeDecoration) return null;
      //We are getting an object (as an string) that indicates wich locations are available depending on the content type
      const str = this.decorationsConfiguratorAttributes?.filter(
        (attr) => attr?.name === "Available Locations For Content Type"
      )[0].value;

      return JSON.parse(str);
    },

    currentLocation() {
      return this.activeDecorationAttributes?.filter(
        (attr) => attr?.name === "Decoration Location Name"
      )[0]?.value;
    },

    currentContentType() {
      return this.activeDecorationAttributes?.filter(
        (attr) => attr?.name === "Content Type"
      )[0]?.value;
    },

    availableLocations() {
      if (!this.activeDecoration) return null;
      return this.availableLocationsByContentType[
        this.currentContentType
      ]?.filter((location) => {
        //Return the location that are not beign used by the current decorations

        const locationsInUse = this.decorations3DListByReversibleView.map(
          (decoration) => decoration.locationName
        );
        const isLocationInUse = locationsInUse.includes(location);
        /*Return :
          1.the locations that are not being used by the current decorations
          2.The original location, because we might need it
        */
        return (
          !isLocationInUse ||
          location === this.activeDecoration?.originalDecorationLocation
        );
      });
    },

    graphic() {
      if (!this.activeDecoration) return null;

      const activeDecorationHasThumbnail =
        this.activeDecoration && this.activeDecoration.thumbnail;

      if (activeDecorationHasThumbnail) {
        //The active decoration is new, so we have to get the preview from the active decoration
        return this.activeDecoration;
      } else {
        //The active decoration is not new, so we have to get the preview from the decorations list
        const locationName = this.activeDecoration?.originalDecorationLocation;
        const index = this.decorations3DListByReversibleView.findIndex(
          (item) => item.locationName === locationName
        );
        return this.decorations3DListByReversibleView[index];
      }
    },

    graphicPreview() {
      return this.graphic?.thumbnail;
    },

    graphicFileName() {
      return this.graphic?.name || this.graphic?.configuration?.["Logo"]?.name;
    },

    flipXConfiguration() {
      if (!this.activeDecoration) return null;
      return this.activeDecorationAttributes?.filter(
        (attr) => attr?.name === "Flip Image X"
      )[0];
    },

    flipYConfiguration() {
      if (!this.activeDecoration) return null;
      return this.activeDecorationAttributes?.filter(
        (attr) => attr?.name === "Flip Image Y"
      )[0];
    },

    materialsConfiguration() {
      if (!this.activeDecoration) return null;
      return this.activeDecorationAttributes?.filter(
        (attr) => attr?.name === "Decoration Material"
      )[0];
    },

    materialsOptions() {
      if (!this.activeDecoration) return null;
      return this.materialsConfiguration?.values?.map((v) => v?.value);
    },

    rotationConfiguration() {
      if (!this.activeDecoration) return null;
      return this.activeDecorationAttributes?.filter(
        (attr) => attr?.name === "Rotation"
      )[0];
    },

    sizeConfiguration() {
      if (!this.activeDecoration) return null;
      return this.activeDecorationAttributes?.filter(
        (attr) => attr?.name === "Text Height"
      )[0];
    },

    sizeOptions() {
      if (!this.activeDecoration) return null;
      return this.sizeConfiguration?.values?.map((v) => v?.value);
    },

    sizeValue() {
      return (
        this.sizeConfiguration &&
        (this.sizeConfiguration?.value ||
          this.sizeConfiguration?.values?.[
            this.sizeConfiguration?.values?.length - 1
          ]?.value)
      );
    },

    textValue() {
      return this.activeDecorationAttributes?.filter(
        (attr) => attr?.name === "Text"
      )[0]?.value;
    },
  },

  methods: {
    ...mapActions("threekit", [
      "changeLogo",
      "createNew3DDecoration",
      "remove3DDecoration",
      "setActiveDecorationChanges",
      "setActiveDecorationLocation",
      "setLoadingState",
      "updateActiveDecorationThumbnail",
    ]),
    ...mapActions("project", ["editProjectLogo", "uploadProjectLogo"]),
    ...mapActions("uploadLogos", ["openEditLogoModalToEditSingleLogo"]),

    //3D Calculation
    decorationIsLogo(decorationType) {
      return decorationType === "Graphics" || decorationType === "Uploads";
    },

    decorationLabel(decorationType) {
      //Take the last word from the content type
      switch (decorationType) {
        case "Custom Text":
          return "tee";
        case "Player Name":
          return "tee-name";
        case "Player Number":
          return "tee-number";
        default:
          return "tee";
      }
    },

    getAvailableLocationsByContentType(contentType) {
      if (!this.activeDecoration) return null;
      return this.availableLocationsByContentType[contentType]?.filter(
        (location) => {
          //Return the location that are not beign used by the current decorations

          const locationsInUse = this.decorations3DListByReversibleView.map(
            (decoration) => decoration.locationName
          );
          const isLocationInUse = locationsInUse.includes(location);
          /*Return :
          1.the locations that are not being used by the current decorations
          2.The original location, because we might need it
        */
          return (
            !isLocationInUse ||
            location === this.activeDecoration?.originalDecorationLocation
          );
        }
      );
    },

    async handleChangeGraphicColor({ color, colorKey }) {
      const threekitColorAssetId = this.getThreekitColorAssetIdByGameplanId(
        color.id
      );
      try {
        this.setLoadingState({
          loadingState: true,
          message: "Applying Changes...",
        });
        const layerConfigurator =
          this.activeGraphicFromLibraryConfigurator?.getNestedConfigurator(
            this.activeGraphicFromLibraryLayers[colorKey]
          );

        await layerConfigurator.setConfiguration({
          Fill: { assetId: threekitColorAssetId },
        });

        await this.updateActiveDecorationThumbnail();
        await this.handleChangeSize(this.sizeValue);
      } catch (error) {
        console.log(error);
      } finally {
        this.setLoadingState({
          loadingState: false,
          message: "Applying Changes...",
        });
      }
    },

    async handleChangeLogo({ logo, skipClearLoading = false }) {
      this.setLoadingState({
        loadingState: true,
        message: "Applying Changes...",
      });
      if (logo.asset_id) {
        setTimeout(() => {
          this.$nextTick(async () => {
            try {
              await this.changeLogo(logo);
              this.showCustomizeLogo = true;
            } catch (error) {
              console.log(error);
            } finally {
              !skipClearLoading &&
                this.setLoadingState({ loadingState: false });
            }
          });
        }, 100);
      }
    },

    async handleSelectGraphic(graphic) {
      this.showGraphicsLibraryModal = false;
      await this.handleChangeLogo({
        logo: {
          asset_id: graphic.id,
          filename: graphic.name,
          contentType: "Graphics",
        },
      });
    },

    async handleChangeLocation(location) {
      await this.setLoadingState({
        loadingState: true,
        message: "Applying Changes...",
      });

      setTimeout(() => {
        this.$nextTick(async () => {
          try {
            //next tick
            await this.setActiveDecorationLocation(location);
          } catch (error) {
            console.error(error);
          } finally {
            this.setLoadingState({ loadingState: false });
          }
        });
      }, 100);
    },

    async handleFlip(axis) {
      this.setLoadingState({
        loadingState: true,
        message: "Applying Changes...",
      });

      const flipConfiguration = this[`flip${axis}Configuration`];

      setTimeout(() => {
        this.$nextTick(async () => {
          try {
            //next tick
            const flipValue = flipConfiguration.value === "Yes" ? "No" : "Yes";
            await this.setActiveDecorationChanges({
              decorationPropertyName: `Flip Image ${axis}`,
              decorationPropertyValue: flipValue,
            });
          } catch (error) {
            console.error(error);
          } finally {
            this.setLoadingState({ loadingState: false });
          }
        });
      }, 100);
    },
    async handleChangeMaterial(material) {
      this.setLoadingState({
        loadingState: true,
        message: "Applying Changes...",
      });
      setTimeout(() => {
        this.$nextTick(async () => {
          try {
            //next tick
            await this.setActiveDecorationChanges({
              decorationPropertyName: "Decoration Material",
              decorationPropertyValue: material,
            });
          } catch (error) {
            console.error(error);
          } finally {
            this.setLoadingState({ loadingState: false });
          }
        });
      }, 100);
    },

    async handleChangeRotation() {
      this.setLoadingState({
        loadingState: true,
        message: "Applying Changes...",
      });
      setTimeout(() => {
        this.$nextTick(async () => {
          try {
            const rotation =
              this.rotationConfiguration?.value ===
              this.rotationConfiguration?.max
                ? this.rotationConfiguration?.min + 90
                : this.rotationConfiguration?.value + 90;
            await this.setActiveDecorationChanges({
              decorationPropertyName: "Rotation",
              decorationPropertyValue: rotation,
            });
          } catch (error) {
            console.error(error);
          } finally {
            this.setLoadingState({ loadingState: false });
          }
        });
      }, 100);
    },

    async handleChangeSize(size) {
      this.setLoadingState({
        loadingState: true,
        message: "Applying Changes...",
      });

      setTimeout(() => {
        this.$nextTick(async () => {
          try {
            //next tick
            await this.setActiveDecorationChanges({
              decorationPropertyName: "Text Height",
              decorationPropertyValue: size,
            });
          } catch (error) {
            console.error(error);
          } finally {
            this.setLoadingState({ loadingState: false });
          }
        });
      }, 100);
    },

    async handleOpenEditLogoModal() {
      const activeLogoAssetId = this.graphic.configuration.Logo.assetId;
      const logo = this.projectLogos.find(
        (projectLogo) => projectLogo.asset_id == activeLogoAssetId
      );
      await this.openEditLogoModalToEditSingleLogo({
        ...logo,
        content: this.graphicPreview,
      });
      this.$nextTick(() => {
        this.showEditLogo = true;
      });
    },

    handleOpenGraphicsLibrary() {
      this.showGraphicsLibraryModal = true;
    },

    async handleSaveLogo({ logo, typesOfLogo }) {
      try {
        await this.setLoadingState({
          loadingState: true,
          message: "Applying Changes...",
        });

        if (!this.showCustomizeLogo) {
          console.log("showCustomizeLogo: ", this.showCustomizeLogo, { logo, typesOfLogo });
          await this.uploadProjectLogo({ logo, typesOfLogo });
        } else {
          console.log("showCustomizeLogo: ", this.showCustomizeLogo);
          this.handleChangeLogo({ logo, skipClearLoading: true });
        }
        await this.updateActiveDecorationThumbnail();
      } catch (error) {
        console.log(error);
      } finally {
        await this.setLoadingState({ loadingState: false });
      }
    },

    async handleSelectTypeOfTextDecoration(textType) {
      this.createNew3DDecoration({ typeOfDecoration: "text", textType });
    },
  },

  mounted() {
    //First, check if there is an active decoration
    if (this.activeDecoration) {
      // When the component is mounted,we need to set the switch to the active decoration type, logo or text
      if (
        this.activeDecoration?.configuration?.["Content Type"] === "Uploads" ||
        this.activeDecoration?.configuration?.["Content Type"] === "Graphics"
      ) {
        this.addDecorationSwitch = "logo";

        //If there is a logo, show the customize dropdowns
        if (this.activeDecoration?.configuration?.["Logo"]?.["assetId"]) {
          this.showCustomizeLogo = true;
        } else {
          this.showCustomizeLogo = false;
          this.showEditLogo = false;
        }
      } else {
        this.addDecorationSwitch = "text";
      }
    } else {
      //New Decoration
      this.addDecorationSwitch = this.layoutAllowsLogo ? "logo" : "text";
      this.showCustomizeLogo = false;
      this.showEditLogo = false;

      this.createNew3DDecoration({
        typeOfDecoration: this.addDecorationSwitch,
      });
    }
  },

  watch: {
    //Create new decoration on switch event
    addDecorationSwitch(newValue) {
      if (newValue === "logo") {
        this.showCustomizeLogo = false;
        this.showEditLogo = false;
      } else {
        this.showCustomizeLogo = false;
        this.showEditLogo = false;
      }
      if (this.activeDecoration) {
        this.createNew3DDecoration({ typeOfDecoration: newValue });
      }
    },

    //When the project logos chage,it means that the user has uploaded a new logo
    async projectLogos(newValue) {
      if (newValue.length > 0) {
        const logo = newValue[newValue.length - 1];
        await this.handleChangeLogo({ logo });
        this.showCustomizeLogo = true;
        this.showEditLogo = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#add-decorations {
  width: 85%;
  #add-decorations-switch {
    background: $grey-lightest;
    border: 1px solid $borderColor;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2rem;
    padding: 0.25rem;

    label {
      background: transparent;
      border-radius: 6px;
      width: 45%;
      margin: 0;
      padding: 0.75rem 1rem;
      display: flex;
      align-items: center;
      justify-content: space-evenly;
      transition: all 0.1s;
      &.selected {
        background: $dark;
        cursor: initial;
        span {
          color: white;
        }
        img {
          filter: invert(100%);
        }
      }

      &:not(.selected):not(.disabled):hover {
        background: $grey-light;
        cursor: pointer;
      }

      &.disabled:not(.selected) {
        background: $grey-lightest;
        cursor: not-allowed;
        span {
          color: $grey-dark;
        }
        img {
          filter: invert(100%);
        }
      }

      input {
        display: none;
      }
    }
  }
  .add-decoration--logo-filename {
    width: 100%;
    margin-bottom: 0.5rem;
    p {
      overflow: hidden;
      max-width: 70%;
      text-transform: uppercase;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  #add-decoration-logo-preview {
    background: $grey-lighter;
    border: 1px solid $borderColor;
    border-radius: 6px;
    display: flex;
    height: 200px;
    justify-content: center;
    margin-bottom: 1rem;
    padding: 1rem;
    position: relative;
    width: 100%;

    #logo-preview-container {
      max-width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      align-items: center;
      #logo-preview-image {
        max-width: 100%;
        height: 100%;
      }
      button.swap-graphic {
        background: $dark;
        position: absolute;
        margin-left: auto;
        margin-right: auto;
        left: 0;
        right: 0;
        text-align: center;
        opacity: 0;
        pointer-events: none;
        width: 50%;
        z-index: -1;
        span {
          color: white;
        }
      }

      &:hover {
        button.swap-graphic {
          opacity: 1;
          pointer-events: initial;
          z-index: 1;
        }
      }
    }

    #logo-tools {
      position: absolute;
      bottom: 0;
      left: 0rem;
      top: 0;
      width: 80px;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      align-items: center;
      .tool-button {
        background: none !important;

        img {
          height: 2rem;
          width: 2rem;
        }

        &.flip-y {
          img {
            transform: rotate(90deg);
          }
        }
      }
    }
  }
  .text-type-buttons-container {
    margin-bottom: 1rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid $grey-light;
    button {
      background: $grey-darker;
      justify-content: space-evenly;
      width: 30%;

      span {
        color: white;
      }
      img {
        filter: invert(1);
      }
      &.unselected,
      &:hover {
        background: $grey-lightest;
        color: $grey-dark;
        border: 1px solid $grey-light;
        span {
          color: $grey-darker;
        }
        img {
          filter: invert(0);
        }
      }

      @media screen and (max-width: 1024px) {
        width: 100%;
        justify-content: start;
        img {
          margin-left: 35%;
          margin-right: 1rem;
        }
      }
    }
  }

  .info-text {
    p,
    span {
      color: $grey-dark;
    }
  }

  .logos-container {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;

    .logo-box {
      cursor: pointer;
      transition: opacity 0.1s;
      &:hover {
        opacity: 0.8;
      }
    }
  }

  #graphics-library-info {
    padding: 1.5rem 3rem;
    background: $blueBackground;
    border: 1px solid $blue;
    border-radius: 15px;
    p {
      margin-bottom: 1rem;
      color: $blue;
      line-height: 1.5rem;
      max-width: 70%;
    }
  }

  .inputs-row-container {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
    width: 90%;
    padding: 0.25rem 0;
    background: white;
    /* border: 1px solid $grey-light; */
    border-radius: 6px;

    .input-label {
      display: flex;
      flex-direction: column;
      margin-bottom: 0;
      width: 100%;

      & > span {
        text-transform: uppercase;
      }
      &.input-in-row {
        width: 40%;
      }

      span {
        margin-bottom: 0.5rem;
      }

      input {
        width: 100%;
        padding: 0.75rem;
        background: white;
        border: 1px solid $grey-light;
        border-radius: 6px;
      }

      &#graphic-location-dropdown,
      &#graphic-material-dropdown {
        width: 30%;
      }
      &#graphic-sizes-dropdown {
        width: 18%;
      }

      @media screen and (max-width: 1440px) {
        &#graphic-location-dropdown,
        &#graphic-material-dropdown,
        &#graphic-sizes-dropdown {
          width: 100%;
        }
      }
    }

    &.select {
      padding: 0;
      border: none;
      ::v-deep {
        .vs__dropdown-toggle {
          border-radius: 6px;
          border: 1px solid $grey-light;
          padding: 0.5rem;
        }
      }
    }

    .v-select {
      ::v-deep .vs__dropdown-toggle {
        border-radius: 6px;
        border: 1px solid $grey-light;
        padding: 0.5rem;
        .vs__selected-options {
          .vs__selected {
            /* color: transparent; */
            background-position: center;
            background-size: contain;
            background-repeat: no-repeat;
            text-transform: capitalize;

            // font-family: "SlateStd-Bold", arial, sans-serif !important;
          }
        }
      }
    }

    @media screen and (max-width: 1440px) {
      flex-flow: column;
      .input-in-row {
        width: 100%;
      }
    }
  }

  ::v-deep .selected-colors {
    .color-container {
      flex-direction: column;
      max-width: 33%;
      align-items: flex-start;
      justify-content: center;
      .color-circle {
        box-shadow: none;
        border: none;
        border-radius: 5px;
        width: 100px;
        height: 50px;
        margin-bottom: 0.95rem;

        .white-border {
          border: none;
        }
        .color-fill {
          border-radius: 5px !important;

          &.none-color::before {
            top: -25px;
            left: 50px;
            height: 200%;
            transform: rotate(65deg);
          }
        }
      }
    }
  }

  @media screen and (max-width: 1440px) {
    width: 90%;
  }
  @media screen and (max-width: 1024px) {
    width: 95%;
  }
}
</style>
